import { Button, Card, Divider, Form, PageHeader, Select, Space, Switch, Typography } from 'antd'
import { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery } from 'react-query'
import cloneDeep from 'lodash.clonedeep'
import FormBlock from '../../components/forms/FormBlock'
import FormSection from '../../components/forms/FormSection'
import FormWrap from '../../components/forms/FormWrap'
import FormField from '../../components/forms/FormField'
import Error from '../../components/Error'
import Loader from '../../components/Loader'
import { ProductSizeForm } from '../../forms/ProductSize'
import { getMeasureFormByProductType } from '../../forms/blocks'
import {
  getProductSizeById,
  updateProductSize,
} from '../../network/ProductSize'
import { getFixedDesignsForSelect } from '../../network/FixedDesign'
import { createUrlWithFiltersFromCurrentURL } from '../../utils/filterUtils'

const { Title } = Typography


const EditProductSize = ({ history }) => {
  const [form] = Form.useForm()
  const { id } = useParams()
  const productType = Form.useWatch('productType', form)
  const isFixedDesign = Form.useWatch('isFixedDesign', form)
  const [measureTemplate, setMeasureTemplate] = useState(null)
  // Track previous productType to only clear fixedDesignId when the user actively changes type
  const prevProductTypeRef = useRef(undefined)

  const fixedDesignsQuery = useQuery(
    ['getFixedDesignsForSelect', { category: productType }],
    getFixedDesignsForSelect,
    {
      refetchOnWindowFocus: false,
      enabled: !!productType && !!isFixedDesign,
    }
  )

  const productSizeQuery = useQuery(
    ['getProductSizeById', { id }],
    getProductSizeById,
    { refetchOnWindowFocus: false, enabled: !!id }
  )

  useEffect(() => {
    if (productSizeQuery.data) {
      form.resetFields()
      const { name, sizes: sizesData, fixedDesignId: fdRaw, ...rest } = productSizeQuery.data
      // fixedDesignId may be a populated object or a plain ID string
      const fixedDesignIdValue =
        fdRaw && typeof fdRaw === 'object' ? (fdRaw.id ?? null) : (fdRaw ?? null)
      form.setFieldsValue({
        ...rest,
        name: {
          en: name?.en ?? '',
          zh: name?.zh ?? '',
        },
        fixedDesignId: fixedDesignIdValue,
        sizes: (sizesData || []).map((size) => ({
          name: {
            en: size.name?.en ?? '',
            zh: size.name?.zh ?? '',
          },
          measurements: size.measurements || {},
        })),
      })
    }
  }, [productSizeQuery.data, form])

  // Clear the fixed design only when the user actively switches from one product type to another.
  // Comparing with the previous value skips the initial null → value transition from data loading.
  useEffect(() => {
    const prev = prevProductTypeRef.current
    prevProductTypeRef.current = productType
    if (prev && productType && prev !== productType) {
      form.setFieldValue('fixedDesignId', undefined)
    }
  }, [productType, form])

  useEffect(() => {
    if (productType) {
      // Keys of measurement fields that should not be shown/edited
      const excludedMeasurementKeys = new Set([
        'trialSamplePicture',
        'trialSampleSize',
        'trialComment',
        'picturesRight',
        'picturesLeft',
      ])

      const measureForm = getMeasureFormByProductType(productType)
      if (measureForm) {
        const tpl = cloneDeep(measureForm)
        // Remove conditional/hiding logic but keep field.key unchanged
        for (const section of tpl.sections || []) {
          // Filter out excluded measurement fields
          section.fields = (section.fields || []).filter((field) => {
            const key = Array.isArray(field.key) ? field.key[0] : null
            if (!key) return true
            return !excludedMeasurementKeys.has(key)
          })

          for (const field of section.fields || []) {
            delete field.isHiding
            delete field.isConditional
            delete field.isInactive
          }
        }
        setMeasureTemplate(tpl)
      } else {
        setMeasureTemplate(null)
      }
    } else {
      setMeasureTemplate(null)
    }
  }, [productType])

  if (productSizeQuery.isError) {
    return (
      <>
        <PageHeader
          className="site-page-header"
          title="Edit product size preset"
          onBack={() =>
            history.push(createUrlWithFiltersFromCurrentURL('/product-sizes'))
          }
        />
        <Error retry={() => productSizeQuery.refetch()} />
      </>
    )
  }

  if (productSizeQuery.isLoading || !productSizeQuery.data) {
    return (
      <>
        <PageHeader
          className="site-page-header"
          title="Edit product size preset"
          onBack={() =>
            history.push(createUrlWithFiltersFromCurrentURL('/product-sizes'))
          }
        />
        <Loader />
      </>
    )
  }

  return (
    <>
      <PageHeader
        className="site-page-header"
        title="Edit product size preset"
        onBack={() =>
          history.push(createUrlWithFiltersFromCurrentURL('/product-sizes'))
        }
        style={{ backgroundColor: '#fff' }}
      />
      <FormWrap
        key={id}
        id={id}
        form={form}
        data={productSizeQuery.data}
        title="update_product_size"
        request={updateProductSize}
        cancelText="Cancel"
        okText="Save"
        onSuccess={() =>
          history.push(createUrlWithFiltersFromCurrentURL('/product-sizes'))
        }
        onCancel={() =>
          history.push(createUrlWithFiltersFromCurrentURL('/product-sizes'))
        }
        cleanBeforeSending={(oldData) => {
          const fixedDesignRaw = oldData.fixedDesignId
          const cleanedData = {
            productType: oldData.productType,
            name: {
              en: oldData.name?.en ?? '',
              zh: oldData.name?.zh ?? '',
            },
            isFixedDesign: !!oldData.isFixedDesign,
            fixedDesignId: oldData.isFixedDesign
              ? (typeof fixedDesignRaw === 'object' && fixedDesignRaw !== null
                  ? fixedDesignRaw.id ?? fixedDesignRaw
                  : fixedDesignRaw ?? null)
              : null,
            sizes: (oldData.sizes || []).map((size) => ({
              name: {
                en: size.name?.en ?? '',
                zh: size.name?.zh ?? '',
              },
              measurements: size.measurements || null,
            })),
          }
          return cleanedData
        }}
        invalidate={['getProductSizes', 'getProductSizeById']}
      >
        <div className="section-from-vertical">
          <FormBlock form={form} formTemplate={ProductSizeForm} />

          <Card
            className="form-wrapper-card"
            style={{ height: 'fit-content', flex: 1 }}
            title={<Title level={4}>Fixed design</Title>}
          >
            <div className="form-field-wrapper nb-columns-2">
              <div className="col-1 row-1 size-1">
                <Form.Item
                  name="isFixedDesign"
                  label="Is a fixed design size"
                  valuePropName="checked"
                  extra={!productType ? 'Select a product type first' : undefined}
                >
                  <Switch
                    checkedChildren="Yes"
                    unCheckedChildren="No"
                    disabled={!productType}
                  />
                </Form.Item>
              </div>
              {isFixedDesign && productType && (
                <div className="col-2 row-1 size-1">
                  <Form.Item
                    name="fixedDesignId"
                    label="Fixed design"
                    rules={[{ required: true, message: 'Please select a fixed design' }]}
                  >
                    <Select
                      showSearch
                      allowClear
                      placeholder="Select a fixed design"
                      loading={fixedDesignsQuery.isLoading}
                      notFoundContent={
                        fixedDesignsQuery.isLoading
                          ? 'Loading…'
                          : 'No fixed designs found for this product type'
                      }
                      filterOption={(input, option) =>
                        option?.children?.toLowerCase().includes(input.toLowerCase())
                      }
                    >
                      {(fixedDesignsQuery.data || []).map((fd) => {
                        const label = fd.name?.en || fd.name?.zh || fd.sku || fd.id
                        return (
                          <Select.Option key={fd.id} value={fd.id}>
                            {label}
                            {fd.sku ? ` — ${fd.sku}` : ''}
                          </Select.Option>
                        )
                      })}
                    </Select>
                  </Form.Item>
                </div>
              )}
            </div>
          </Card>

          <Card
            className="form-wrapper-card"
            style={{ height: 'fit-content', flex: 1 }}
            title={<Title level={4}>Sizes</Title>}
          >
            <Form.List name="sizes">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <div key={key}>
                      <Space
                        style={{
                          display: 'flex',
                          marginBottom: 16,
                        }}
                        align="baseline"
                      >
                        <Title level={5}>Size {name + 1}</Title>
                        {fields.length > 0 && (
                          <Button
                            type="link"
                            danger
                            onClick={() => remove(name)}
                          >
                            Remove
                          </Button>
                        )}
                      </Space>
                      
                      <div className="form-field-wrapper nb-columns-2">
                        <div className="col-1 row-1 size-1">
                          <FormField
                            {...restField}
                            field={{
                              label: 'Size name (EN)',
                              key: ['name', 'en'],
                              isRequired: true,
                              placeholder: 'e.g. Small',
                            }}
                            name={name}
                            form={form}
                            blockKey={[]}
                            prev={[]}
                          />
                        </div>
                        <div className="col-2 row-1 size-1">
                          <FormField
                            {...restField}
                            field={{
                              label: 'Size name (中文)',
                              key: ['name', 'zh'],
                              isRequired: false,
                              placeholder: 'e.g. 小号',
                            }}
                            name={name}
                            form={form}
                            blockKey={[]}
                            prev={[]}
                          />
                        </div>
                      </div>

                      {measureTemplate && productType && (
                        <div style={{ marginTop: 16 }}>
                          {measureTemplate.sections?.map((section, sectionIndex) => {
                            // We're inside Form.List with name="sizes", so Form.Item names are relative to sizes[name]
                            // blockKey should be ['measurements'] so Form.Item name becomes [name, 'measurements', ...field.key]
                            // But we need to pass name={name} to FormSection so it can use it in FormField
                            const sizeBlockKey = ['measurements']
                            return (
                              <FormSection
                                key={`size-${name}-section-${sectionIndex}`}
                                form={form}
                                section={section}
                                blockKey={sizeBlockKey}
                                prev={sizeBlockKey}
                                channel={['sizes']}
                                fieldKeyPrefix={`size-${name}`}
                                dataRecieved={() => {}}
                                sectionKey={name}
                              />
                            )
                          })}
                        </div>
                      )}

                      {name < fields.length - 1 && <Divider />}
                    </div>
                  ))}

                  <Form.Item>
                    <Button
                      type="dashed"
                      onClick={() => add()}
                      block
                      disabled={!productType}
                      style={{
                        borderColor: '#1890ff',
                        color: '#1890ff',
                      }}
                    >
                      + Add Size
                    </Button>
                  </Form.Item>
                </>
              )}
            </Form.List>
          </Card>
        </div>
      </FormWrap>
    </>
  )
}

export default EditProductSize
